import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { Crown, Sparkles, ArrowUpRight, AlertTriangle } from "lucide-react";

const DIM_LABELS = {
  fr: {
    career: "Carrière",
    international_mobility: "Mobilité internationale",
    professional_stability: "Stabilité professionnelle",
    financial_security: "Sécurité financière",
    social_benefits: "Avantages sociaux",
    gov_programs_access: "Accès aux programmes publics",
    project_financing_capacity: "Capacité de financement",
    quality_of_life: "Qualité de vie",
    work_life_balance: "Équilibre travail-vie",
    family_situation: "Situation familiale",
    personal_objectives: "Objectifs personnels",
  },
  en: {
    career: "Career",
    international_mobility: "International mobility",
    professional_stability: "Professional stability",
    financial_security: "Financial security",
    social_benefits: "Social benefits",
    gov_programs_access: "Public programs access",
    project_financing_capacity: "Financing capacity",
    quality_of_life: "Quality of life",
    work_life_balance: "Work-life balance",
    family_situation: "Family situation",
    personal_objectives: "Personal objectives",
  },
};

const QUESTIONS_FR = [
  "Quelle est la meilleure trajectoire pour moi ?",
  "Quel pays correspond le mieux à mon profil ?",
  "Quel métier maximise revenus + sécurité financière + chances migratoires ?",
  "Quelle certification obtenir en priorité ?",
  "Quel est le meilleur investissement en temps et en argent ?",
];

export default function LifeStrategy() {
  const { t, lang } = useI18n();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/life-strategy/status");
        setMeta(data);
      } catch {}
    })();
  }, []);

  const dimLabels = DIM_LABELS[lang];

  return (
    <div>
      <TopBar
        title="ALTIORA Life Strategy Engine™"
        subtitle={lang === "fr" ? "Le moteur transversal au-dessus de tous les autres" : "The top-level engine above all others"}
      />
      <div className="px-6 md:px-10 py-8 space-y-8">

        {/* Hero card — the ONE strategy positioning */}
        <div className="relative overflow-hidden rounded-md border border-[#0B2545]/40 bg-gradient-to-br from-[#EBE8DE] via-[#FFFFFF] to-[#F8F7F3] p-8 md:p-10 shadow-[0_0_80px_-30px_rgba(197,168,128,0.6)]">
          <div className="absolute top-6 right-6 flex items-center gap-2 rounded-md border border-[#0B2545]/40 bg-[#0B2545]/8 px-3 py-1">
            <Crown className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#0B2545]">Meta · orchestrateur</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#0B2545]">
            Life Strategy · v1.0 architecture
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tighter text-[#1F2937] leading-tight max-w-3xl">
            {lang === "fr"
              ? "Pas une liste de recommandations."
              : "Not a list of recommendations."}
            <br />
            <span className="text-[#0B2545]">
              {lang === "fr" ? "UNE stratégie cohérente." : "ONE coherent strategy."}
            </span>
          </h2>
          <p className="mt-6 text-sm text-[#5B5B62] leading-relaxed max-w-3xl">
            {lang === "fr"
              ? "Le Life Strategy Engine™ orchestre les 9 autres moteurs d'ALTIORA pour optimiser simultanément 11 dimensions de votre trajectoire économique. Il résout les trade-offs (salaire élevé vs sécurité migratoire), détecte les incohérences et produit une stratégie hiérarchisée par impact global."
              : "The Life Strategy Engine™ orchestrates ALTIORA's 9 other engines to jointly optimise 11 dimensions of your economic trajectory. It resolves trade-offs (high salary vs immigration safety), detects inconsistencies, and produces a strategy hierarchised by global impact."}
          </p>
        </div>

        {/* Dimensions grid — the 11 optimised dimensions */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-4">
            {lang === "fr" ? "11 dimensions optimisées simultanément" : "11 dimensions jointly optimised"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(dimLabels).map(([k, label], idx) => (
              <div key={k} className="rounded-md border border-[#E4E1D8] bg-white p-4 hover:border-[#B8B5AC] transition-colors">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="mt-1 text-sm text-[#1F2937]">{label}</div>
                <div className="mt-1 font-mono text-[9px] text-[#0B2545]">{k}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Orchestrated engines */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
            {lang === "fr" ? "Moteurs orchestrés" : "Orchestrated engines"} · {meta?.orchestrated_engines?.length || 9}
          </div>
          <div className="flex flex-wrap gap-2">
            {(meta?.orchestrated_engines || [
              "identity_core","career_intelligence","opportunity_engine",
              "hidden_opportunity_engine","career_simulation","global_mobility",
              "knowledge_graph","learning_memory","decision_engine",
            ]).map((k) => (
              <span key={k} className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62] border border-[#E4E1D8] rounded px-2 py-1">
                {k}
              </span>
            ))}
          </div>
        </div>

        {/* Questions cardinales */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
            {lang === "fr" ? "Questions complexes résolues" : "Complex questions solved"}
          </div>
          <div className="space-y-2">
            {(meta?.questions_answered || QUESTIONS_FR).map((q, i) => (
              <div key={i} className="flex items-start gap-3">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#0B2545]" strokeWidth={1.5} />
                <div className="text-sm text-[#1F2937]">{q}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Financial disclaimer — always visible */}
        <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 flex gap-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D4AF37] mb-1">
              {lang === "fr" ? "Avertissement · Capacité de financement" : "Disclaimer · Financing capacity"}
            </div>
            <p className="text-xs text-[#5B5B62] leading-relaxed">
              {(lang === "fr" ? meta?.financial_capacity_disclaimer_fr : meta?.financial_capacity_disclaimer_en) ||
                (lang === "fr"
                  ? "Les estimations de capacité de financement sont basées sur des hypothèses et ne constituent en aucun cas un conseil financier, fiscal ou juridique. Consultez un professionnel accrédité pour toute décision financière importante."
                  : "Financing capacity estimates are based on hypotheses and do NOT constitute financial, tax or legal advice. Consult a licensed professional for any material financial decision.")}
            </p>
          </div>
        </div>

        {/* Coming soon banner */}
        <div className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] p-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-md border border-[#0B2545]/25 bg-[#0B2545]/8 px-3 py-1">
            <Crown className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#0B2545]">
              {lang === "fr" ? "Module planifié · v1.7" : "Planned module · v1.7"}
            </span>
          </div>
          <p className="mt-4 text-sm text-[#5B5B62] max-w-2xl mx-auto">
            {lang === "fr"
              ? "L'architecture est prête et documentée. L'activation orchestrera automatiquement les 9 moteurs pour produire votre stratégie unique dès l'ouverture."
              : "Architecture is ready and documented. Activation will automatically orchestrate the 9 engines to produce your unique strategy at rollout."}
          </p>
        </div>
      </div>
    </div>
  );
}
